import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardHeader, Button, Collapse, CardBody, FormGroup, Label, Input, FormFeedback } from 'reactstrap';
import BoolInput from '../../../../../misc/BoolInput';
import EnumInput from '../../../../../misc/EnumInput';
import SelectInput from '../../../../../misc/SelectInput';
import Skola24FilterInput from '../Skola24FilterInput';
import Skola24ScheduleOrganizationExport from './Skola24ScheduleOrganizationExport';
import { CoreSkola24ScheduleConfiguration } from '../../../../../../models/CoreSkola24ScheduleConfigurationModels';
import { ScheduleEventNaming, ScheduleImportIntervalRule } from './Skola24ScheduleConfigurationTypes';


export const validator = (configuration: CoreSkola24ScheduleConfiguration, active: boolean) => {
    const validation: string[] = [];

    if (active) {
        if (!configuration.hostname || configuration.hostname === "") {
            validation.push('hostname');
        }
        if (!configuration.eventNaming) {
            validation.push('eventNaming');
        }

        if (!configuration.importIntervalRule) {
            validation.push('importIntervalRule');
        }
    }
    return validation;
}

interface Skola24ScheduleConfigurationProps {
    disabled: boolean
    configuration: CoreSkola24ScheduleConfiguration
    updatePart: (part: Partial<CoreSkola24ScheduleConfiguration>) => void
    active: boolean
    onValidChange: (arg: boolean) => void 
}


const Skola24ScheduleConfiguration = (props: Skola24ScheduleConfigurationProps) => {
    const { t } = useTranslation(['configuration', 'common']);

    const [open, setOpen] = useState<boolean>(true);

    const validation = validator(props.configuration, props.active);

    useEffect(() => {
        props.onValidChange(validation.length ? true : false)
    }, [props.configuration, props.active]);

    return (
        <Card className="edit_module">
            <CardHeader tag="h2">
                <Button outline color="secondary" style={{ width: '37px', float: 'right' }} onClick={() => setOpen(!open)}>{open ? '-' : '+'}</Button>
                {t('modules.Skola24Schedule.title', { ns: 'common' })}
            </CardHeader>
            <Collapse isOpen={open}>
                <CardBody>
                    <div style={{ margin: '1rem 0' }}>
                        <FormGroup>
                            <p>{t('skola24Schedule.accessInformation', { ns: 'configuration' })}</p>
                            <p>{t('skola24Schedule.runRestrictions.information', { ns: 'configuration' })}</p>
                            <ul>
                                <li>{t('skola24Schedule.runRestrictions.timeOfDay', { ns: 'configuration' })}</li>
                                <li>{t('skola24Schedule.runRestrictions.timeOfDayExceptions', { ns: 'configuration' })}</li>
                            </ul>
                        </FormGroup>
                        <FormGroup>
                            <Label tag="h5" for="hostname">{t('skola24Schedule.host.label', { ns: 'configuration' })}</Label>
                            <Input
                                type="text"
                                name="hostname"
                                id="hostname"
                                placeholder={t('skola24Schedule.host.placeholder', { ns: 'configuration' })}
                                disabled={props.disabled}
                                value={props.configuration.hostname || ''}
                                onChange={e => props.updatePart({ hostname: e.target.value })}
                                invalid={validation.indexOf('hostname') !== -1}
                                autoComplete="off"
                            />
                            <FormFeedback>{t('skola24Schedule.host.formFeedback', { ns: 'configuration' })}</FormFeedback>
                        </FormGroup>

                        <FormGroup>
                            <Label tag="h5"> {t('skola24Schedule.importIntervalRule.label', { ns: 'configuration' })}</Label>
                            <SelectInput
                                items={ScheduleImportIntervalRule.map((rule) => ({
                                    value: rule,
                                    text: t(`skola24Schedule.importIntervalRule.options.${rule}`, { ns: 'configuration' })
                                }))}
                                onChange={(name, value) => props.updatePart({ [name]: value })}
                                name='importIntervalRule'
                                selected={Number(props.configuration.importIntervalRule)}
                                defaultText={t('skola24Schedule.importIntervalRule.options.placeholder', { ns: 'configuration' })}
                                defaultValue={0}
                                disableSort={true}
                                invalid={validation.indexOf('importIntervalRule') !== -1}
                                invalidFeedback={t('skola24Schedule.importIntervalRule.invalidFeedback')}
                                disableDefaultOption={true}
                                hideDefaultOptionIfNotSet={true}
                            />
                        </FormGroup>

                        <EnumInput
                            title={t('skola24Schedule.eventNaming.title', { ns: 'configuration' })}
                            name='eventNaming'
                            value={props.configuration.eventNaming}
                            options={ScheduleEventNaming.map(x => ({
                                value: x,
                                text: t(`skola24Schedule.eventNaming.options.${x}`, { ns: 'configuration' })
                            }))}
                            onChange={(name, value) => props.updatePart({ [name]: value })}
                            disabled={props.disabled}
                            invalid={validation.indexOf('eventNaming') !== -1}
                            invalidFeedback={t('skola24Schedule.eventNaming.formFeedback', { ns: 'configuration' })}
                        />

                        <BoolInput
                            title={t('skola24Schedule.requireOrganizer', { ns: 'configuration' })}
                            name="requireOrganizer"
                            value={props.configuration.requireOrganizer}
                            updateValue={(name, value) => props.updatePart({ [name]: value })}
                            disabled={props.disabled}
                        />

                        <FormGroup>
                            <h5>{t('skola24Schedule.eventFilters.title', { ns: 'configuration' })} </h5>
                            <p>{t('skola24Schedule.eventFilters.description', { ns: 'configuration' })} </p>
                            <Skola24FilterInput
                                name="eventFilters"
                                filters={props.configuration.eventFilters}
                                onChange={(name, value) => props.updatePart({ [name]: value })}
                                disabled={props.disabled} />
                        </FormGroup>

                        <Skola24ScheduleOrganizationExport
                            organizationExport={props.configuration.organizationExport}
                            onChange={(value) => props.updatePart({ organizationExport: value })}
                            disabled={props.disabled}
                        />
                    </div>
                </CardBody>
            </Collapse>
        </Card>)
}

export default Skola24ScheduleConfiguration;
